import {
  type UpdateBackupRp,
  configureBackupRpOperation,
  fetchBackupRp,
  fetchDatabasesByEnvId,
  selectLatestBackupRpByEnvId,
  selectPitrEnabledDatabasesByEnvId,
  updateBackupRp,
} from "@app/deploy";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { environmentActivityUrl, supportUrl } from "@app/routes";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { useValidator } from "../../hooks";
import { Banner, BannerMessages } from "../banner";
import { Box } from "../box";
import { Button, ButtonAdmin } from "../button";
import { PitrMisconfigurationWarning } from "../db/pitr-warnings";
import {
  DetailHeader,
  DetailInfoItem,
  DetailTitleBar,
} from "../detail-page-header-view";
import { FormGroup } from "../form-group";
import { Group } from "../group";
import { IconEdit } from "../icons";
import { Input } from "../input";
import { Radio, RadioGroup } from "../select";
import { Switch } from "../switch";
import { tokens } from "../tokens";

interface BackupRetentionPolicyData {
  key: string;
  value: string | number;
  helpText?: string;
  docsUrl?: string;
}

const validators = {
  daily: (data: UpdateBackupRp) => {
    const txt = "Number of daily backups";
    const value = data.daily;

    if (Number.isNaN(value)) {
      return `${txt} must be a number`;
    }
    if (value <= 0) {
      return `${txt} backups must be > 0`;
    }
    if (value % 1 !== 0) {
      return `${txt} must be whole number`;
    }
  },
  monthly: (data: UpdateBackupRp) => {
    const txt = "Number of monthly backups";
    const value = data.monthly;

    if (Number.isNaN(value)) {
      return `${txt} must be a number`;
    }
    if (value < 0) {
      return `${txt} must be >= 0`;
    }
    if (value % 1 !== 0) {
      return `${txt} must be whole number`;
    }
  },
  yearly: (data: UpdateBackupRp) => {
    const txt = "Number of yearly backups";
    const value = data.yearly;

    if (Number.isNaN(value)) {
      return `${txt} must be a number`;
    }
    if (value < 0) {
      return `${txt} must be >= 0`;
    }
    if (value % 1 !== 0) {
      return `${txt} must be whole number`;
    }
  },
  pitrDays: (data: UpdateBackupRp) => {
    const txt = "Number of PITR days";
    const value = data.pitrDays;

    if (Number.isNaN(value)) {
      return `${txt} must be a number`;
    }
    if (value < 0) {
      return `${txt} must be >= 0`;
    }
    if (value > 1) {
      return "Only 1 day of PITR data may be retained during beta";
    }
    if (value % 1 !== 0) {
      return `${txt} must be whole number`;
    }
  },
};

export const BackupRpView = ({ envId }: { envId: string }) => {
  const { isLoading } = useQuery(fetchBackupRp({ envId }));
  const backupRp = useSelector((s) =>
    selectLatestBackupRpByEnvId(s, { envId }),
  );
  const [editing, setEditing] = useState(false);

  const retentionData: BackupRetentionPolicyData[] = [
    { key: "Daily backups retained", value: backupRp.daily },
    { key: "Monthly backups retained", value: backupRp.monthly },
    { key: "Yearly backups retained", value: backupRp.yearly },
  ];

  const policyData: BackupRetentionPolicyData[] = [
    {
      key: "Copy backups to another region",
      value: backupRp.makeCopy ? "Yes" : "No",
    },
    {
      key: "Keep final backup for deprovisioned databases",
      value: backupRp.keepFinal ? "Yes" : "No",
    },
  ];

  const pitrData: BackupRetentionPolicyData[] = [
    {
      key: "Point-in-time Recovery for PostgreSQL",
      value: backupRp.pitrDays > 0 ? "Enabled" : "Disabled",
    },
    {
      key: "Days of PITR data retained",
      value: backupRp.pitrDays,
    },
  ];

  if (isLoading && !editing) {
    return null;
  }

  if (!editing) {
    return (
      <DetailHeader>
        {backupRp.daily <= backupRp.pitrDays ? (
          <PitrMisconfigurationWarning />
        ) : null}
        <DetailTitleBar title="Backup Retention Policy" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex flex-col gap-4 pr-4">
            {retentionData.map((item) => (
              <DetailInfoItem key={item.key} title={item.key}>
                {item.value}
              </DetailInfoItem>
            ))}
          </div>

          <div className="flex flex-col gap-4 pr-4">
            {policyData.map((item) => (
              <DetailInfoItem key={item.key} title={item.key}>
                {item.value}
              </DetailInfoItem>
            ))}
          </div>

          <div className="flex flex-col gap-4 pr-4">
            {pitrData.map((item) => (
              <DetailInfoItem key={item.key} title={item.key}>
                <div>
                  {item.value}
                  {item.helpText && (
                    <div className="text-sm mt-1 text-black">
                      <div>{item.helpText}</div>
                      {item.docsUrl && (
                        <div>
                          <Link to={item.docsUrl} target="_blank">
                            View Docs
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </DetailInfoItem>
            ))}
          </div>
        </div>

        <hr className="my-2" />

        <div>
          <ButtonAdmin
            envId={envId}
            onClick={() => setEditing(true)}
            variant="white"
            type="button"
          >
            <IconEdit variant="sm" className="mr-2" />
            Edit Policy
          </ButtonAdmin>
        </div>
      </DetailHeader>
    );
  }

  return <BackupRpEditor envId={envId} onClose={() => setEditing(false)} />;
};

export const BackupRpEditor = ({
  envId,
  onClose,
}: { envId: string; onClose: () => void }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  useQuery(fetchBackupRp({ envId }));
  const backupRp = useSelector((s) =>
    selectLatestBackupRpByEnvId(s, { envId }),
  );
  useQuery(fetchDatabasesByEnvId({ envId }));
  const pitrEnabledDbs = useSelector((s) =>
    selectPitrEnabledDatabasesByEnvId(s, { envId }),
  );

  const [daily, setDaily] = useState(backupRp.daily);
  const [monthly, setMonthly] = useState(backupRp.monthly);
  const [yearly, setYearly] = useState(backupRp.yearly);
  const [pitrDays, setPitrDays] = useState(backupRp.pitrDays);
  const [makeCopy, setMakeCopy] = useState(backupRp.makeCopy ? "yes" : "no");
  const [keepFinal, setKeepFinal] = useState(backupRp.keepFinal ? "yes" : "no");
  const [reduced, setReduced] = useState(false);
  const [errors, validate] = useValidator<UpdateBackupRp, typeof validators>(
    validators,
  );
  const data: UpdateBackupRp = {
    daily,
    monthly,
    yearly,
    pitrDays,
    makeCopy: makeCopy === "yes",
    keepFinal: keepFinal === "yes",
    id: backupRp.id,
    envId,
  };
  const action = updateBackupRp(data);
  const backupRpOperatonAction = configureBackupRpOperation({
    pitrDays,
    envId,
  });
  const loader = useLoader(updateBackupRp);
  const opLoader = useLoader(backupRpOperatonAction);

  const hasDBWithPITR = useMemo(() => {
    return pitrEnabledDbs.length > 0;
  }, [pitrEnabledDbs]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate(data)) return;
    dispatch(action);

    if (pitrDays !== backupRp.pitrDays) {
      dispatch(backupRpOperatonAction);
    }
  };

  useLoaderSuccess(opLoader, () => {
    navigate(environmentActivityUrl(envId));
  });

  const onReset = () => {
    setDaily(backupRp.daily);
    setMonthly(backupRp.monthly);
    setYearly(backupRp.yearly);
    setPitrDays(backupRp.pitrDays);
    setMakeCopy(backupRp.makeCopy ? "yes" : "no");
    setKeepFinal(backupRp.keepFinal ? "yes" : "no");
  };

  useEffect(() => {
    onReset();
  }, [backupRp]);

  useEffect(() => {
    setReduced(
      daily < backupRp.daily ||
        monthly < backupRp.monthly ||
        yearly < backupRp.yearly ||
        pitrDays < backupRp.pitrDays ||
        (makeCopy === "no" && backupRp.makeCopy) ||
        (keepFinal === "no" && backupRp.keepFinal),
    );
  }, [backupRp, daily, monthly, yearly, pitrDays, makeCopy, keepFinal]);

  return (
    <Box>
      <Group>
        <h3 className={tokens.type.h3}>Backup Retention Policy</h3>

        <BannerMessages {...loader} />

        <form onSubmit={onSubmit} className="flex flex-col gap-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-20">
            <div className="flex flex-col gap-4">
              <FormGroup
                label="Daily backups retained"
                htmlFor="daily"
                labelProps={{
                  className: "text-lg font-semibold text-gray-900",
                }}
                feedbackMessage={errors.daily}
                feedbackVariant={errors.daily ? "danger" : "info"}
              >
                <Input
                  id="daily"
                  type="number"
                  value={daily}
                  onChange={(e) =>
                    setDaily(Number.parseInt(e.currentTarget.value, 10))
                  }
                />
                <p className="text-sm mt-1 text-black">
                  Recommendation for production environments: 14-30 daily;
                  non-production environments: 1-14 daily
                </p>
              </FormGroup>

              <FormGroup
                label="Monthly backups retained"
                htmlFor="monthly"
                labelProps={{
                  className: "text-lg font-semibold text-gray-900",
                }}
                feedbackMessage={errors.monthly}
                feedbackVariant={errors.monthly ? "danger" : "info"}
              >
                <Input
                  id="monthly"
                  type="number"
                  value={monthly}
                  onChange={(e) => {
                    setMonthly(Number.parseInt(e.currentTarget.value, 10));
                  }}
                />
                <p className="text-sm mt-1 text-black">
                  Recommendation for production environments: 12 monthly;
                  non-production environments: 0 monthly
                </p>
              </FormGroup>

              <FormGroup
                label="Yearly backups retained"
                htmlFor="yearly"
                labelProps={{
                  className: "text-lg font-semibold text-gray-900",
                }}
                feedbackMessage={errors.yearly}
                feedbackVariant={errors.yearly ? "danger" : "info"}
              >
                <Input
                  id="yearly"
                  type="number"
                  value={yearly}
                  onChange={(e) => {
                    setYearly(Number.parseInt(e.currentTarget.value, 10));
                  }}
                />
                <p className="text-sm mt-1 text-black">
                  Recommendation for production environments: 5 yearly;
                  non-production environments: 0 yearly
                </p>
              </FormGroup>
            </div>

            <div className="flex flex-col gap-4">
              <FormGroup
                label="Copy backups to another region"
                htmlFor="make-copy"
                labelProps={{
                  className: "text-lg font-semibold text-gray-900",
                }}
              >
                <RadioGroup
                  name="make-copy"
                  selected={makeCopy}
                  onSelect={(inp) => setMakeCopy(inp)}
                >
                  <Radio value="yes">Yes</Radio>
                  <Radio value="no">No</Radio>
                </RadioGroup>
                <p className="text-sm mt-1 text-black">
                  When enabled, Aptible will copy all the backups within that
                  Environment to another region
                </p>
              </FormGroup>

              <FormGroup
                label="Keep final backup for deprovisioned databases"
                htmlFor="keep-final"
                labelProps={{
                  className: "text-lg font-semibold text-gray-900",
                }}
              >
                <RadioGroup
                  name="keep-final"
                  selected={keepFinal}
                  onSelect={(inp) => setKeepFinal(inp)}
                >
                  <Radio value="yes">Yes</Radio>
                  <Radio value="no">No</Radio>
                </RadioGroup>
                <p className="text-sm mt-1 text-black">
                  When enabled, Aptible will retain the last backup of a
                  Database after you deprovision it
                </p>
              </FormGroup>
            </div>

            <div className="flex flex-col gap-4">
              <FormGroup
                label="Point-in-time Recovery for PostgreSQL"
                htmlFor="pitr-enabled-indicator"
                labelProps={{
                  className: "text-lg font-semibold text-gray-900",
                }}
                description={
                  pitrDays === 0 ? (
                    <p>Configure PITR days below to enable.</p>
                  ) : undefined
                }
              >
                <Switch
                  checked={pitrDays > 0}
                  disabled={true}
                  onChange={() => {}}
                  className="text-sm"
                >
                  Enabled
                </Switch>
              </FormGroup>

              <FormGroup
                label="Days of PITR data retained"
                htmlFor="pitr-days"
                labelProps={{
                  className: "text-lg font-semibold text-gray-900",
                }}
                feedbackMessage={errors.pitrDays}
                feedbackVariant={errors.pitrDays ? "danger" : "info"}
              >
                <Input
                  id="pitr-days"
                  type="number"
                  min={hasDBWithPITR ? 1 : 0}
                  value={pitrDays}
                  disabled={pitrDays > 0}
                  onChange={(e) => {
                    setPitrDays(Number.parseInt(e.currentTarget.value, 10));
                  }}
                />
                <p className="text-sm mt-1 text-black">
                  Need more than one day?{" "}
                  <Link to={supportUrl()} target="_blank" className="underline">
                    Let us know
                  </Link>{" "}
                </p>
              </FormGroup>
            </div>
          </div>

          <hr className="my-2" />

          {reduced ? (
            <Banner variant="warning">
              Warning: Reducing the number of retained backups, including
              disabling copies or final backups, will automatically delete
              existing, automated backups that do not match the new policy. This
              may result in the permanent loss of backup data and could violate
              your organization's internal compliance controls.
            </Banner>
          ) : null}

          {daily <= pitrDays ? <PitrMisconfigurationWarning /> : null}

          <div className="flex gap-2">
            <ButtonAdmin
              type="submit"
              envId={envId}
              isLoading={loader.isLoading}
            >
              Save Policy
            </ButtonAdmin>
            <Button type="button" onClick={onClose} variant="white">
              Cancel
            </Button>
          </div>
        </form>
      </Group>
    </Box>
  );
};
